@if (isLoading()) {
  <cor-skeleton height="18rem" />
} @else {
  <div
    class="card"
    test-id="activity-log-card">
    <div class="card-header">
      <div class="card-title">
        <h5>Activity Log</h5>
      </div>
      <p-selectButton
        test-id="timeline-on-filterby-user"
        size="small"
        [(ngModel)]="filterBy"
        [unselectable]="true"
        (click)="onFilterbyUser()"
        [options]="[
          { text: 'All Users', value: 'all', icon: 'pi pi-users' },
          { text: 'Current User', value: 'one', icon: 'pi pi-user' },
        ]"
        optionLabel="text"
        optionValue="value">
        <ng-template
          let-option
          pTemplate="item">
          <div class="tw:flex tw:items-center tw:gap-1">
            <i [class]="option.icon"></i>
            <span>{{ option.text }}</span>
          </div>
        </ng-template>
      </p-selectButton>
    </div>
    <div class="card-body">
      @if (logs.length > 0) {
        <p-timeline [value]="logs">
          <ng-template
            #opposite
            let-log>
            <div
              class="tw:text-muted-color"
              style="font-family: monospace">
              {{ log.createdDate | localDate }}
            </div>
          </ng-template>
          <ng-template
            #marker
            let-log>
            <cor-status-icon
              [status]="log.newStatus || log.status"
              [showText]="false" />
          </ng-template>
          <ng-template
            #content
            let-log>
            <div class="tw:flex tw:flex-col tw:gap-2 tw:pb-5">
              <div class="tw:flex tw:flex-col tw:gap-2">
                <ng-container *ngTemplateOutlet="logTemplate; context: { log: log }" />
                <!-- comment -->
                @if (log.comment) {
                  <small class="tw:flex tw:flex-col tw:gap-2 tw:text-muted-color tw:font-normal">
                    <strong>Comment</strong>
                    <cor-milkdown
                      [ngModel]="log.comment"
                      disabled />
                  </small>
                }
              </div>
            </div>
          </ng-template>
        </p-timeline>
      } @else {
        <cor-no-data class="col-12" />
      }
    </div>
  </div>
}

<ng-template
  #logTemplate
  let-log="log">
  @if (log.action === 'Changed') {
    <div class="tw:flex tw:items-center tw:gap-2">
      <span>Changes made to the object by</span>
      <div class="tw:flex tw:items-center tw:gap-1">
        <p-avatar
          class="tw:shrink-0"
          [label]="log.actorName | nameBadge"
          style="--p-avatar-width: 1.4rem; --p-avatar-height: 1.4rem; --p-avatar-font-size: 0.75rem"
          [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
          shape="circle" />
        <span>
          <b>{{ log.actorName }}</b>
        </span>
      </div>
    </div>
  } @else if (log.actorName === SYSTEM_APPROVALS.WORKFLOW_RULE) {
    @switch (log.newStatus || log.status) {
      <!-- Pending Approval -->
      @case (WORKFLOW_STATUS.PENDING_APPROVAL) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Status Changed to</span>
          <cor-status-icon [status]="WORKFLOW_STATUS.PENDING_APPROVAL" />
        </div>
      }
      <!-- Approved -->
      @case (WORKFLOW_STATUS.APPROVED) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Status Changed to</span>
          <cor-status-icon [status]="WORKFLOW_STATUS.APPROVED" />
        </div>
      }
      <!-- Rejected -->
      @case (WORKFLOW_STATUS.REJECTED) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Status Changed to</span>
          <cor-status-icon [status]="WORKFLOW_STATUS.REJECTED" />
        </div>
      }

      <!-- Default -->
      @default {
        <div class="tw:text-muted-color tw:text-sm">{{ log.message }}</div>
      }
    }
  } @else {
    @switch (log.newStatus || log.status) {
      <!-- Created -->
      @case (WORKFLOW_STATUS.DRAFT) {
        @if (log.action === AUDIT_ACTION.CREATED) {
          <div class="tw:flex tw:items-center tw:gap-2">
            <span>Created by</span>
            <div class="tw:flex tw:items-center tw:gap-1">
              <p-avatar
                class="tw:shrink-0"
                [label]="log.actorName | nameBadge"
                style="
                  --p-avatar-width: 1.4rem;
                  --p-avatar-height: 1.4rem;
                  --p-avatar-font-size: 0.75rem;
                "
                [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
                shape="circle" />
              <span>
                <b>{{ log.actorName }}</b>
              </span>
            </div>
          </div>
        } @else {
          <div class="tw:flex tw:items-center tw:gap-2">
            <span>Status Changed to</span>
            <cor-status-icon [status]="WORKFLOW_STATUS.DRAFT" />
            <span>by</span>
            <div class="tw:flex tw:items-center tw:gap-1">
              <p-avatar
                class="tw:shrink-0"
                [label]="log.actorName | nameBadge"
                style="
                  --p-avatar-width: 1.4rem;
                  --p-avatar-height: 1.4rem;
                  --p-avatar-font-size: 0.75rem;
                "
                [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
                shape="circle" />
              <span>
                <b>{{ log.actorName }}</b>
              </span>
            </div>
          </div>
        }
      }

      <!-- Requested for approval -->
      @case (REVIEW_STATUS.PENDING_ACCEPTANCE) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Approval Request submitted by</span>
          @if (log.actorName === SYSTEM_APPROVALS.WORKFLOW_RULE) {
            <b>{{ log.actorName }}</b>
          } @else {
            <div class="tw:flex tw:items-center tw:gap-1">
              <p-avatar
                class="tw:shrink-0"
                [label]="log.actorName | nameBadge"
                style="
                  --p-avatar-width: 1.4rem;
                  --p-avatar-height: 1.4rem;
                  --p-avatar-font-size: 0.75rem;
                "
                [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
                shape="circle" />
              <span>
                <b>{{ log.actorName }}</b>
              </span>
            </div>
          }
          <span>to</span>
          @if (log.recipientName === SYSTEM_APPROVALS.ALL) {
            <b>
              {{ log.recipientName }}
              @if (log.recipientResponsibility) {
                ({{ log.recipientResponsibility }})
              }
            </b>
          } @else {
            <div class="tw:flex tw:items-center tw:gap-1">
              <p-avatar
                class="tw:shrink-0"
                [label]="log.recipientName | nameBadge"
                style="
                  --p-avatar-width: 1.4rem;
                  --p-avatar-height: 1.4rem;
                  --p-avatar-font-size: 0.75rem;
                "
                [ngStyle]="{
                  'background-color': log.recipientName | uniqueRgbColor,
                  color: 'white',
                }"
                shape="circle" />
              <span>
                <b>{{ log.recipientName }}</b>
              </span>
            </div>
          }
        </div>
      }

      <!-- Need Changes -->
      @case (REVIEW_STATUS.NEED_CHANGES) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Status Changed to</span>
          <cor-status-icon [status]="REVIEW_STATUS.NEED_CHANGES" />
          <span>by</span>
          <div class="tw:flex tw:items-center tw:gap-1">
            <p-avatar
              class="tw:shrink-0"
              [label]="log.actorName | nameBadge"
              style="
                --p-avatar-width: 1.4rem;
                --p-avatar-height: 1.4rem;
                --p-avatar-font-size: 0.75rem;
              "
              [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
              shape="circle" />
            <span>
              <b>{{ log.actorName }}</b>
            </span>
          </div>
        </div>
      }

      <!-- Need Info -->
      @case (REVIEW_STATUS.NEED_INFO) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Status Changed to</span>
          <cor-status-icon [status]="REVIEW_STATUS.NEED_INFO" />
          <span>by</span>
          <div class="tw:flex tw:items-center tw:gap-1">
            <p-avatar
              class="tw:shrink-0"
              [label]="log.actorName | nameBadge"
              style="
                --p-avatar-width: 1.4rem;
                --p-avatar-height: 1.4rem;
                --p-avatar-font-size: 0.75rem;
              "
              [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
              shape="circle" />
            <span>
              <b>{{ log.actorName }}</b>
            </span>
          </div>
        </div>
      }

      <!-- Comment added-->
      @case (REVIEW_STATUS.ADD_COMMENT) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Comment Added by</span>
          <div class="tw:flex tw:items-center tw:gap-1">
            <p-avatar
              class="tw:shrink-0"
              [label]="log.actorName | nameBadge"
              style="
                --p-avatar-width: 1.4rem;
                --p-avatar-height: 1.4rem;
                --p-avatar-font-size: 0.75rem;
              "
              [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
              shape="circle" />
            <span>
              <b>{{ log.actorName }}</b>
            </span>
          </div>
          <span>to</span>
          <div class="tw:flex tw:items-center tw:gap-1">
            @if (log.recipientName === SYSTEM_APPROVALS.ALL) {
              <b>
                {{ log.recipientName }}
                @if (log.recipientResponsibility) {
                  ({{ log.recipientResponsibility }})
                }
              </b>
            } @else {
              <p-avatar
                class="tw:shrink-0"
                [label]="log.recipientName | nameBadge"
                style="
                  --p-avatar-width: 1.4rem;
                  --p-avatar-height: 1.4rem;
                  --p-avatar-font-size: 0.75rem;
                "
                [ngStyle]="{
                  'background-color': log.recipientName | uniqueRgbColor,
                  color: 'white',
                }"
                shape="circle" />
              <span>
                <b>{{ log.recipientName }}</b>
              </span>
            }
          </div>
        </div>
      }

      <!-- Accepted -->
      @case (REVIEW_STATUS.ACCEPTED) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Approval Request</span>
          <cor-status-icon [status]="REVIEW_STATUS.ACCEPTED" />
          <span>by</span>
          <div class="tw:flex tw:items-center tw:gap-1">
            <p-avatar
              class="tw:shrink-0"
              [label]="log.actorName | nameBadge"
              style="
                --p-avatar-width: 1.4rem;
                --p-avatar-height: 1.4rem;
                --p-avatar-font-size: 0.75rem;
              "
              [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
              shape="circle" />
            <span>
              <b>{{ log.actorName }}</b>
            </span>
          </div>
        </div>
      }

      <!-- Rejected -->
      @case (REVIEW_STATUS.REJECTED) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Approval Request</span>
          <cor-status-icon [status]="REVIEW_STATUS.REJECTED" />
          <span>by</span>
          <div class="tw:flex tw:items-center tw:gap-1">
            <p-avatar
              class="tw:shrink-0"
              [label]="log.actorName | nameBadge"
              style="
                --p-avatar-width: 1.4rem;
                --p-avatar-height: 1.4rem;
                --p-avatar-font-size: 0.75rem;
              "
              [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
              shape="circle" />
            <span>
              <b>{{ log.actorName }}</b>
            </span>
          </div>
        </div>
      }

      <!-- Cancelled -->
      @case (REVIEW_STATUS.CANCELED) {
        <div class="tw:flex tw:items-center tw:gap-2">
          <span>Approval Request</span>
          <cor-status-icon [status]="REVIEW_STATUS.CANCELED" />
          <span>by</span>
          <div class="tw:flex tw:items-center tw:gap-1">
            <p-avatar
              class="tw:shrink-0"
              [label]="log.actorName | nameBadge"
              style="
                --p-avatar-width: 1.4rem;
                --p-avatar-height: 1.4rem;
                --p-avatar-font-size: 0.75rem;
              "
              [ngStyle]="{ 'background-color': log.actorName | uniqueRgbColor, color: 'white' }"
              shape="circle" />
            <span>
              <b>{{ log.actorName }}</b>
            </span>
          </div>
        </div>
      }

      <!-- Default -->
      @default {
        <div class="tw:text-muted-color tw:text-sm">{{ log.message }}</div>
      }
    }
  }
</ng-template>
